'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notFound, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { fieldErrorsFromApiError, isNotFoundApiError } from '@/lib/api-error';
import { blankToNull, sameSeoCopy, trackSearchCopy } from '@/lib/seo-text';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import {
  AlertDialog,
  Body,
  Button,
  Card,
  Caption,
  Dialog,
  DialogFooter,
  DialogSection,
  Field,
  Heading,
  I,
  InfoCallout,
  Input,
  Label,
  PageHeader,
  Row,
  ScreenBody,
  Skeleton,
  SkeletonGroup,
  SkeletonText,
  Stack,
  Textarea,
  Toggle,
  useDelayedLoading,
} from '@leanin/ui';
import { Sortable, type SortableItem } from '@leanin/ui/client';

import { SeoMetaFields } from '../seo';
import { TypeaheadMultiSelect } from '../admin/TypeaheadMultiSelect';
import { LearningTrackResourceRow } from './LearningTrackResourceRow';
import { TrackCoverUploadField } from './TrackCoverUploadField';
import {
  searchResourcesForPicker,
  useLearningTrack,
  type LearningTrackItem,
  type LearningTrackResourcePickerItem,
} from '@/lib/queries/learning-tracks';
import {
  useAttachLearningTrackResource,
  useDeleteLearningTrack,
  useDetachLearningTrackResource,
  useReorderLearningTrackResources,
  useUpdateLearningTrack,
  type UpdateLearningTrackInput,
} from '@/lib/mutations/learning-tracks';

type Props = {
  readonly trackId: string;
};

type DraftHeader = {
  readonly title: string;
  readonly description: string;
  // '' is unset; saved as null so search falls back to title / description.
  readonly seoTitle: string;
  readonly seoDescription: string;
  readonly published: boolean;
};

type SortableTrackItem = SortableItem & {
  readonly raw: LearningTrackItem;
};

export function LearningTrackAdminView({ trackId }: Props) {
  const t = useTranslations('LearningTracks');
  const trackQuery = useLearningTrack(trackId, { retry: false, retryOnMount: false });
  const navigate = useNavigate();

  const updateMutation = useUpdateLearningTrack();
  const deleteMutation = useDeleteLearningTrack();
  const attachMutation = useAttachLearningTrackResource(trackId);
  const detachMutation = useDetachLearningTrackResource(trackId);
  const reorderMutation = useReorderLearningTrackResources(trackId);

  const [draft, setDraft] = useState<DraftHeader | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState<
    ReadonlyArray<LearningTrackResourcePickerItem>
  >([]);
  const [pickedIds, setPickedIds] = useState<ReadonlyArray<string>>([]);
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  // Dismissal guard for the attach flight. Set synchronously in the click
  // handler — `attaching` is render state, and an Escape keydown can land
  // before its pending render commits.
  const attachInFlightRef = useRef(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Seed once: a refetch only carries back what this form saved, and
  // re-seeding would clobber an edit in flight.
  useEffect(() => {
    if (trackQuery.data === undefined) return;
    if (draft !== null) return;
    setDraft({
      title: trackQuery.data.title,
      description: trackQuery.data.description,
      seoTitle: trackQuery.data.seoTitle ?? '',
      seoDescription: trackQuery.data.seoDescription ?? '',
      published: trackQuery.data.published,
    });
  }, [trackQuery.data, draft]);

  const srStrings = useMemo(
    () => ({
      onPickUp: (item: string) => t('admin.reorderSr.pickUp', { item }),
      onMove: (item: string, position: number, total: number) =>
        t('admin.reorderSr.move', { item, position, total }),
      onDrop: (item: string, position: number) =>
        t('admin.reorderSr.drop', { item, position }),
      onCancel: (item: string) => t('admin.reorderSr.cancel', { item }),
    }),
    [t],
  );

  const items = useMemo<ReadonlyArray<LearningTrackItem>>(
    () =>
      trackQuery.data
        ? [...trackQuery.data.items].sort((a, b) => a.sortOrder - b.sortOrder)
        : [],
    [trackQuery.data],
  );

  const sortableItems: ReadonlyArray<SortableTrackItem> = useMemo(
    () =>
      items.map((it) => ({
        id: it.resourceId,
        label:
          it.resource === null
            ? t('resourceRow.removedTitle')
            : it.resource.title,
        raw: it,
      })),
    [items, t],
  );

  const handleReorder = useCallback(
    (next: ReadonlyArray<SortableTrackItem>) => {
      reorderMutation.mutate(next.map((it) => String(it.id)));
    },
    [reorderMutation],
  );

  const handleSave = useCallback(() => {
    if (draft === null || trackQuery.data === undefined) return;
    setSaveError(null);
    setFieldErrors({});
    const trimmedTitle = draft.title.trim();
    if (trimmedTitle.length === 0) {
      setSaveError(t('admin.titleRequired'));
      return;
    }
    const nextSeoTitle = blankToNull(draft.seoTitle);
    const nextSeoDescription = blankToNull(draft.seoDescription);
    const server = trackQuery.data;
    const patch: UpdateLearningTrackInput = {
      ...(trimmedTitle !== server.title ? { title: trimmedTitle } : {}),
      ...(draft.description !== server.description
        ? { description: draft.description }
        : {}),
      ...(sameSeoCopy(draft.seoTitle, server.seoTitle) ? {} : { seoTitle: nextSeoTitle }),
      ...(sameSeoCopy(draft.seoDescription, server.seoDescription)
        ? {}
        : { seoDescription: nextSeoDescription }),
      ...(draft.published !== server.published
        ? { published: draft.published }
        : {}),
    };
    if (Object.keys(patch).length === 0) return;
    updateMutation.mutate(
      { id: trackId, patch },
      {
        onError: (err) => {
          const serverFieldErrors = fieldErrorsFromApiError(err);
          if (serverFieldErrors !== null) setFieldErrors(serverFieldErrors);
          setSaveError(t('admin.saveFailed'));
        },
      },
    );
  }, [draft, trackQuery.data, trackId, updateMutation, t]);

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(trackId, {
      onSuccess: () => {
        void navigate({ to: '/admin/resources' });
      },
    });
  }, [deleteMutation, trackId, navigate]);

  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setPicked([]);
    setPickedIds([]);
    setAttachError(null);
  }, []);

  const handleAttachPicked = useCallback(async () => {
    if (picked.length === 0 || attachInFlightRef.current) return;
    attachInFlightRef.current = true;
    setAttaching(true);
    setAttachError(null);
    // Server appends at the end when sortOrder is omitted. Attach in
    // series so the resulting order matches the picker order; stop at the
    // first failure so a retry resumes from that point in the same order.
    const attached = new Set<string>();
    let failed: LearningTrackResourcePickerItem | null = null;
    try {
      for (const item of picked) {
        try {
          await attachMutation.mutateAsync({ resourceId: item.id });
          attached.add(item.id);
        } catch {
          failed = item;
          break;
        }
      }
    } finally {
      attachInFlightRef.current = false;
      setAttaching(false);
    }
    if (failed === null) {
      closePicker();
      return;
    }
    // Keep the failed item and everything after it selected for retry.
    const remaining = picked.filter((item) => !attached.has(item.id));
    setPicked(remaining);
    setPickedIds(remaining.map((item) => item.id));
    setAttachError(t('admin.picker.error', { title: failed.title }));
  }, [picked, attachMutation, closePicker, t]);

  const isLoading =
    trackQuery.isPending || trackQuery.data === undefined || draft === null;
  const showSkeleton = useDelayedLoading(isLoading);
  if (isNotFoundApiError(trackQuery.error)) return <NotFoundPage />;
  if (trackQuery.isError) throw notFound();
  if (showSkeleton) {
    return <LearningTrackAdminSkeleton />;
  }
  if (isLoading) return null;

  const track = trackQuery.data;

  const titleError = fieldErrors['title']?.[0];
  const descriptionError = fieldErrors['description']?.[0];
  const seoTitleError = fieldErrors['seoTitle']?.[0];
  const seoDescriptionError = fieldErrors['seoDescription']?.[0];

  const dirty =
    draft.title.trim() !== track.title ||
    draft.description !== track.description ||
    !sameSeoCopy(draft.seoTitle, track.seoTitle) ||
    !sameSeoCopy(draft.seoDescription, track.seoDescription) ||
    draft.published !== track.published;

  return (
    <ScreenBody
      gap="none"
      style={{ background: 'var(--background)', minHeight: '100%' }}
    >
      <PageHeader
        back={{
          label: t('admin.back'),
          href: '/admin/resources',
        }}
        eyebrow={t('admin.eyebrow')}
        title={draft.title.length > 0 ? draft.title : t('admin.untitled')}
        actions={
          <Row gap="sm" wrap>
            <Button
              variant="secondary"
              size="md"
              icon={I.trash}
              onClick={() => setDeleteConfirmOpen(true)}
              loading={deleteMutation.isPending}
            >
              {t('admin.delete')}
            </Button>
            <Button
              variant="primary"
              size="md"
              icon={I.check}
              onClick={handleSave}
              disabled={!dirty}
              loading={updateMutation.isPending}
            >
              {draft.published ? t('admin.publish') : t('admin.saveDraft')}
            </Button>
          </Row>
        }
      />

      <Stack gap="2xl" pt="xl">
        {saveError !== null ? (
          <InfoCallout tone="poppy" eyebrow={t('admin.saveErrorEyebrow')}>
            {saveError}
          </InfoCallout>
        ) : null}
        <Card pad="default">
          <Stack gap="lg">
            <Heading level={3}>{t('admin.details')}</Heading>
            <Field
              label={t('admin.titleLabel')}
              description={t('admin.titleHelp')}
              required
              {...(titleError ? { error: titleError } : {})}
            >
              <Input
                value={draft.title}
                onChange={(e) =>
                  setDraft({ ...draft, title: e.target.value })
                }
                placeholder={t('admin.titlePlaceholder')}
                invalid={titleError !== undefined}
              />
            </Field>
            <TrackCoverUploadField
              trackId={track.id}
              currentCoverUrl={track.coverImageUrl}
            />
            <Field
              label={t('admin.descriptionLabel')}
              description={t('admin.descriptionHelp')}
              {...(descriptionError ? { error: descriptionError } : {})}
            >
              <Textarea
                value={draft.description}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
                rows={3}
                placeholder={t('admin.descriptionPlaceholder')}
                invalid={descriptionError !== undefined}
              />
            </Field>
            <Row gap="md" align="center" justify="space-between">
              <Stack gap="xs">
                <Label>
                  {t('admin.publishImmediately')}
                </Label>
                <Caption>
                  {t('admin.publishOff')}
                </Caption>
              </Stack>
              <Toggle
                on={draft.published}
                onChange={() =>
                  setDraft({ ...draft, published: !draft.published })
                }
              />
            </Row>
          </Stack>
        </Card>

        <Card pad="default">
          <Stack gap="lg">
            <Heading level={3}>{t('admin.seoHeading')}</Heading>
            <SeoMetaFields
              title={{
                label: t('admin.seoTitle'),
                help: t('admin.seoTitleHelp'),
                value: draft.seoTitle,
                onChange: (value) => setDraft({ ...draft, seoTitle: value }),
                error: seoTitleError,
              }}
              description={{
                label: t('admin.seoDescription'),
                help: t('admin.seoDescriptionHelp'),
                value: draft.seoDescription,
                onChange: (value) =>
                  setDraft({ ...draft, seoDescription: value }),
                error: seoDescriptionError,
              }}
              preview={trackSearchCopy({
                title: draft.title,
                seoTitle: draft.seoTitle,
                seoDescription: draft.seoDescription,
                description: draft.description,
                resourceCount: track.publicResourceCount,
              })}
            />
          </Stack>
        </Card>

        <Card pad="default">
          <Stack gap="lg">
            <Row justify="space-between" align="center">
              <Heading level={3}>
                {t('admin.resourcesHeading', { count: items.length })}
              </Heading>
              <Button
                variant="primary"
                size="md"
                icon={I.plus}
                onClick={() => setPickerOpen(true)}
              >
                {t('admin.addResources')}
              </Button>
            </Row>

            {items.length === 0 ? (
              <Body color="mute">{t('admin.emptyBody')}</Body>
            ) : (
              <Sortable<SortableTrackItem>
                items={[...sortableItems]}
                onReorder={handleReorder}
                srStrings={srStrings}
              >
                {(item, dragHandle, reorderButton) => (
                  <LearningTrackResourceRow
                    item={item.raw}
                    dragHandle={dragHandle}
                    reorderButton={reorderButton}
                    onRemove={() =>
                      detachMutation.mutate(item.raw.resourceId)
                    }
                    removing={
                      detachMutation.isPending &&
                      detachMutation.variables === item.raw.resourceId
                    }
                  />
                )}
              </Sortable>
            )}
          </Stack>
        </Card>
      </Stack>

      <Dialog
        open={pickerOpen}
        onOpenChange={(open) => {
          if (open) setPickerOpen(true);
          else closePicker();
        }}
        canDismiss={() => !attachInFlightRef.current && !attaching}
        title={t('admin.picker.title')}
        description={t('admin.picker.description')}
      >
        <DialogSection scrollable>
          <Stack gap="md">
            <Field
              label={t('admin.picker.fieldLabel')}
              description={t('admin.picker.fieldHelp')}
            >
              <TypeaheadMultiSelect<LearningTrackResourcePickerItem>
                placeholder={t('admin.picker.placeholder')}
                fetcher={searchResourcesForPicker}
                selectedIds={pickedIds}
                selectedItems={picked}
                onChange={(ids, next) => {
                  setPickedIds(ids);
                  setPicked(next);
                }}
                renderItem={(item) => (
                  <Stack gap="xs">
                    <Body size="sm">{item.title}</Body>
                    <Caption>{item.kind}</Caption>
                  </Stack>
                )}
                renderSelectedLabel={(item) => item.title}
                emptyState={t('admin.picker.empty')}
              />
            </Field>
            {attachError !== null ? (
              <InfoCallout tone="poppy" eyebrow={t('admin.picker.errorEyebrow')}>
                {attachError}
              </InfoCallout>
            ) : null}
          </Stack>
        </DialogSection>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={closePicker}
            disabled={attaching}
          >
            {t('admin.picker.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleAttachPicked();
            }}
            disabled={attaching || picked.length === 0}
          >
            {attaching
              ? t('admin.picker.adding')
              : t('admin.picker.add', { count: picked.length })}
          </Button>
        </DialogFooter>
      </Dialog>

      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('admin.deleteConfirm.title')}
        description={t('admin.deleteConfirm.description')}
        actionLabel={t('admin.deleteConfirm.action')}
        cancelLabel={t('admin.deleteConfirm.cancel')}
        destructive
        onAction={handleDelete}
      />
    </ScreenBody>
  );
}

export function LearningTrackAdminSkeleton() {
  const t = useTranslations('LearningTracks');
  return (
    <SkeletonGroup label={t('admin.loading')}>
      <ScreenBody
        gap="none"
        style={{ background: 'var(--background)', minHeight: '100%' }}
      >
        <Stack gap="md" pt="lg">
          <Skeleton width={120} height={14} rounded="sm" />
          <Skeleton width="60%" height={40} rounded="sm" />
        </Stack>
        <Stack gap="2xl" pt="xl">
          <Card pad="default">
            <Stack gap="lg">
              <Skeleton width={120} height={24} rounded="sm" />
              <SkeletonText lines={3} size="md" />
            </Stack>
          </Card>
          <Card pad="default">
            <Stack gap="lg">
              <Skeleton width={120} height={24} rounded="sm" />
              <SkeletonText lines={4} size="md" />
            </Stack>
          </Card>
        </Stack>
      </ScreenBody>
    </SkeletonGroup>
  );
}
